import { useState, useEffect } from "react";
import { useFeedback } from "../components/ActionFeedback";
import { motion } from "framer-motion";
import API from "../api/axios";
import Lottie from "lottie-react";
import noDataAnimation from "../layout/no_data.json";


export default function Dashboard() {
  const [selectedDate, setSelectedDate] = useState(new Date().getDate());
  const [agendaDate, setAgendaDate]     = useState(new Date());
  const [chartView, setChartView]       = useState('Month');
  const { showToast, showModal } = useFeedback();
  const [events, setEvents] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [eventsRes, regsRes] = await Promise.all([
        API.get("/events"),
        API.get("/registrations")
      ]);
      setEvents(eventsRes.data);
      setRegistrations(regsRes.data);
    } catch (err) {
      showToast("Failed to load dashboard data", "error");
    } finally {
      setLoading(false);
    }
  };

  const last7Days = Array.from({length: 7}).map((_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (6 - i));
    return d;
  });

  const chartHeights = last7Days.map(date => {
    return registrations.filter(r => {
      if (!r.createdAt) return false;
      const rd = new Date(r.createdAt);
      return rd.getDate() === date.getDate() && rd.getMonth() === date.getMonth() && rd.getFullYear() === date.getFullYear();
    }).length;
  });
  
  const maxChartVal = Math.max(...chartHeights, 1);
  const normalizedHeights = chartHeights.map(c => Math.max((c / maxChartVal) * 120, 10));

  // Aggregated Stats
  const totalAttendees = registrations.length;
  
  const getFilteredRegistrationsCount = () => {
    const now = new Date();
    return registrations.filter(r => {
      const regDate = new Date(r.createdAt);
      if (chartView === 'Week') {
        const weekAgo = new Date();
        weekAgo.setDate(now.getDate() - 7);
        return regDate >= weekAgo;
      } else if (chartView === 'Season') {
        // approx 3 months
        const seasonAgo = new Date();
        seasonAgo.setMonth(now.getMonth() - 3);
        return regDate >= seasonAgo;
      } else {
        // Month
        return regDate.getMonth() === now.getMonth() && regDate.getFullYear() === now.getFullYear();
      }
    }).length;
  };
  const filteredCount = getFilteredRegistrationsCount();
  
  const totalCapacity = events.reduce((acc, curr) => acc + (curr.capacity || 0), 0);
  const goalPercentage = totalCapacity > 0 ? Math.round((totalAttendees / totalCapacity) * 100) : 0;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } }
  };

  const handleChartToggle = (t) => {
    setChartView(t);
  };

  const handleGoalDetails = () => {
    showModal({
      title: "Goal Progress Insights",
      subtitle: "Annual Attendee Target Breakdown",
      content: (
        <div className="space-y-4">
          <div className="flex justify-between border-b pb-2">
            <span className="font-bold text-slate-500">Current Registrations</span>
            <span className="font-black">{totalAttendees.toLocaleString()}</span>
          </div>
          <div className="flex justify-between border-b pb-2">
            <span className="font-bold text-slate-500">Total Capacity</span>
            <span className="font-black">{totalCapacity.toLocaleString()}</span>
          </div>
          <div className="flex justify-between border-b pb-2">
            <span className="font-bold text-slate-500">Remaining Capacity</span>
            <span className="font-black text-amber-500">{Math.max(0, totalCapacity - totalAttendees).toLocaleString()}</span>
          </div>
          <p className="text-xs text-slate-400 mt-4 italic">You have reached {goalPercentage}% of your total event capacity across all active events.</p>
        </div>
      ),
    });
  };

  const handleAgendaClick = (e) => {
    showModal({
      title: e.title,
      subtitle: "Event Details",
      content: (
        <div className="space-y-4 text-center">
          <div className="text-6xl mb-4"><svg xmlns="http://www.w3.org/2000/svg" className="w-16 h-16 text-slate-300 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg></div>
          <p className="text-sm font-bold text-slate-600">Scheduled Time: <span className="text-[#0a0c10] font-black">{e.time}</span></p>
          <p className="text-xs text-slate-500">Location: {e.location || "Main Hall A"}</p>
          <p className="text-xs text-slate-500 mt-4">Capacity is currently at {e.capacity > 0 ? Math.round(((e.registered || 0) / e.capacity) * 100) : 0}% for this session ({e.registered || 0} / {e.capacity}).</p>
        </div>
      ),
    });
  };

  return (
    <motion.div 
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="space-y-12"
    >
      {/* Top Section (Dark Area) */}
      <div className="grid grid-cols-12 gap-8">
        <motion.div variants={itemVariants} className="col-span-12 lg:col-span-9">
          <h2 className="text-white text-xl font-black mb-8 px-2 tracking-tight">Registration Capacity</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {loading ? (
              [1, 2, 3].map(i => <div key={i} className="puzzle-card-dark h-48 animate-pulse"></div>)
            ) : events.length === 0 ? (
               <div className="col-span-3 text-slate-500 font-bold uppercase text-xs p-12 text-center border border-white/5 rounded-3xl">No events found in database.</div>
            ) : events.slice(0, 3).map((event, idx) => {
              const registered = event.registeredCount || 0;
              const total = event.capacity || 1;
              const percentage = Math.round((registered / total) * 100);
              return (
                <motion.div 
                  key={event._id} 
                  variants={itemVariants}
                  whileHover={{ scale: 1.02 }}
                  className="puzzle-card-dark flex flex-col justify-between group cursor-pointer border border-white/5 transition-colors hover:border-amber-400/30" 
                  onClick={() => showToast(`Navigating to ${event.name} details...`, 'success')}
                >
                  <div className="flex justify-between items-start mb-12">
                    <div className="space-y-1">
                      <h3 className="text-white font-black text-lg group-hover:text-amber-400 transition-colors uppercase tracking-tight">{event.name}</h3>
                      <p className="text-slate-500 text-xs font-bold uppercase tracking-widest">{registered} / {total} REGISTERED</p>
                    </div>
                    <div className="w-10 h-10 bg-white/5 rounded-full flex items-center justify-center border border-white/10 group-hover:bg-white group-hover:text-[#0a0c10] transition-all">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" /></svg>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="flex justify-between items-end">
                      <div className="progress-bar-container w-full h-1 mr-4">
                        <motion.div 
                          initial={{ width: 0 }}
                          animate={{ width: `${percentage}%` }}
                          transition={{ duration: 1.5, ease: "easeOut", delay: 0.5 + (idx * 0.1) }}
                          className="bg-white h-full" 
                        ></motion.div>
                      </div>
                      <span className="text-white font-black text-2xl tracking-tighter">{percentage}%</span>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        <motion.div variants={itemVariants} className="col-span-12 lg:col-span-3">
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-white text-xl font-black tracking-tight">Daily Check-ins</h2>
            <div className="flex gap-1">
              <span className="w-1.5 h-1.5 bg-slate-700 rounded-full"></span>
              <span className="w-1.5 h-1.5 bg-slate-700 rounded-full"></span>
            </div>
          </div>
          <div className="puzzle-card-dark h-[240px] flex items-end justify-between px-6 pb-6 relative cursor-pointer group overflow-hidden" onClick={() => {
            showModal({
              title: "Recent Check-ins",
              subtitle: "Latest registrations and check-ins",
              content: (
                <div className="space-y-4 max-h-64 overflow-y-auto">
                  {registrations.slice(0, 10).map((reg, idx) => (
                    <div key={idx} className="flex justify-between items-center p-3 border-b border-slate-100 last:border-0">
                      <div>
                        <p className="text-sm font-bold text-slate-800">{reg.user_id?.name || reg.user_name || "Unknown User"}</p>
                        <p className="text-[10px] text-slate-400 uppercase font-black">{reg.event_name || reg.event_id}</p>
                      </div>
                      <span className={`text-[10px] font-black uppercase px-2 py-1 rounded-md ${reg.status === 'checked-in' ? 'bg-green-100 text-green-600' : 'bg-slate-100 text-slate-500'}`}>
                        {reg.status || 'registered'}
                      </span>
                    </div>
                  ))}
                  {registrations.length === 0 && <p className="text-xs text-slate-400 text-center py-4">No recent check-ins.</p>}
                </div>
              ),
            });
          }}>
            <motion.div 
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.8 }}
              className="absolute top-8 right-8 bg-amber-400 text-[#0a0c10] px-3 py-1 rounded-lg text-[10px] font-black shadow-xl"
            >
              +{filteredCount} This {chartView}
            </motion.div>
            {normalizedHeights.map((h, i) => (
              <div key={i} className="flex flex-col items-center gap-3">
                <motion.div 
                  initial={{ height: 0 }}
                  animate={{ height: h }}
                  transition={{ duration: 1, ease: "backOut", delay: 0.5 + (i * 0.1) }}
                  className={`w-3 rounded-full transition-all duration-300 uppercase ${i === 6 ? "bg-amber-400 shadow-lg shadow-amber-400/20" : "bg-slate-800 group-hover:bg-slate-700"}`}
                ></motion.div>
                <span className="text-[10px] font-bold text-slate-600 uppercase">{last7Days[i].toLocaleDateString('en-US', { weekday: 'narrow' })}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Bottom Section (Light Area) */}
      <div className="grid grid-cols-12 gap-8 pt-12">
        {/* Progress Chart */}
        <motion.div variants={itemVariants} className="col-span-12 lg:col-span-6 puzzle-card-light relative overflow-hidden">
          <div className="flex justify-between items-start mb-12">
            <div>
              <h2 className="text-slate-800 text-xl font-black tracking-tight uppercase">Attendee Growth</h2>
              <div className="flex gap-12 mt-4">
                <div>
                  <p className="text-3xl font-black tracking-tighter">{totalAttendees.toLocaleString()}</p>
                  <p className="text-[10px] text-slate-400 font-bold uppercase mt-1">Total Attendees</p>
                </div>
                <div className="border-l border-slate-100 pl-12 transition-all">
                  <p className="text-3xl font-black tracking-tighter">+{filteredCount}</p>
                  <p className="text-[10px] text-slate-400 font-bold uppercase mt-1">New This {chartView}</p>
                </div>
              </div>
            </div>
            <div className="flex bg-slate-100 p-1 rounded-xl">
              {['Season', 'Month', 'Week'].map(t => (
                <button 
                  key={t} 
                  onClick={() => handleChartToggle(t)}
                  className={`px-4 py-1.5 rounded-lg text-[10px] font-black transition-all hover:bg-white/50 ${chartView === t ? "bg-white text-slate-800 shadow-sm" : "text-slate-400"}`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
          
          <div className="h-64 relative mt-12 cursor-pointer" onClick={() => showToast("Opening advanced chart view", 'success')}>
             <svg className="w-full h-full overflow-visible">
              <motion.path 
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{ pathLength: 1, opacity: 1 }}
                transition={{ duration: 2, ease: "easeInOut", delay: 0.5 }}
                d="M0,150 Q150,50 300,100 T600,80 T900,120" 
                fill="none" 
                stroke="#f59e0b" 
                strokeWidth="4" 
                strokeLinecap="round" 
              />
              <motion.path 
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{ pathLength: 1, opacity: 0.5 }}
                transition={{ duration: 2, ease: "easeInOut", delay: 0.8 }}
                d="M0,180 Q200,100 400,150 T800,90" 
                fill="none" 
                stroke="#94a3b8" 
                strokeWidth="2" 
                strokeDasharray="8,8" 
              />
              <motion.line 
                initial={{ scaleY: 0 }}
                animate={{ scaleY: 1 }}
                transition={{ delay: 1.5 }}
                x1="300" y1="0" x2="300" y2="250" 
                stroke="#cbd5e1" 
                strokeWidth="2" 
                strokeDasharray="4,4" 
                className="origin-bottom"
              />
              <motion.circle 
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", delay: 2.2 }}
                cx="300" cy="100" r="6" 
                fill="#f59e0b" 
                stroke="white" 
                strokeWidth="3" 
              />
            </svg>
            
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 2.5 }}
              className="absolute top-1/2 left-1/3 -translate-x-1/2 bg-[#0a0c10] text-white p-4 rounded-2xl shadow-2xl z-20"
            >
              <p className="text-[10px] text-slate-400 font-bold uppercase mb-2">Sign-up Peak</p>
              <div className="space-y-1">
                <div className="flex justify-between gap-8">
                  <span className="text-xs font-bold">Total Attendees:</span>
                  <span className="text-xs font-black">{totalAttendees.toLocaleString()}</span>
                </div>
                <div className="flex justify-between gap-8 border-t border-white/5 pt-1 mt-1">
                  <span className="text-xs font-bold text-slate-400">Pace Status:</span>
                  <span className="text-xs font-black text-slate-400">{goalPercentage >= 50 ? 'On Track' : 'Needs Attention'}</span>
                </div>
              </div>
            </motion.div>
          </div>
          
          <div className="flex justify-between px-2 pt-8">
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(m => (
              <span key={m} className="text-[10px] font-bold text-slate-400 uppercase">{m}</span>
            ))}
          </div>
        </motion.div>

        {/* Success Rate */}
        <motion.div variants={itemVariants} className="col-span-12 lg:col-span-3 puzzle-card-light flex flex-col">
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-slate-800 text-xl font-black tracking-tight uppercase">Goal Progress</h2>
            <span className="text-[10px] font-black bg-amber-100 px-3 py-1 rounded-full text-amber-700 uppercase">On Track</span>
          </div>
          
          <div className="flex-1 space-y-8">
            <div className="rounded-[2rem] overflow-hidden aspect-video relative group bg-[#0a0c10] flex items-center justify-center">
              <div className="relative">
                 <svg className="w-32 h-32 transform -rotate-90">
                    <circle cx="64" cy="64" r="50" stroke="#1e293b" strokeWidth="12" fill="none" />
                    <motion.circle 
                      initial={{ strokeDashoffset: 314 }}
                      animate={{ strokeDashoffset: 314 - (314 * (goalPercentage / 100)) }}
                      transition={{ duration: 2, ease: "easeOut", delay: 1 }}
                      cx="64" cy="64" r="50" 
                      stroke="#f59e0b" 
                      strokeWidth="12" 
                      fill="none" 
                      strokeDasharray="314" 
                    />
                 </svg>
                 <div className="absolute inset-0 flex items-center justify-center flex-col">
                    <motion.span 
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 2.5 }}
                      className="text-white font-black text-2xl tracking-tighter"
                    >
                      {goalPercentage}%
                    </motion.span>
                    <span className="text-[8px] text-slate-500 font-bold uppercase">Success</span>
                 </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7h8m0 0v8m0-8l-9 9-4-4-6 6" /></svg>
                <p className="font-bold text-slate-500 uppercase text-[10px] tracking-widest leading-none">Registration Target</p>
              </div>
              <h3 className="text-2xl font-black text-slate-800 leading-tight">{totalAttendees.toLocaleString()} / {(totalCapacity / 1000).toFixed(0)}k</h3>
              <p className="text-xs font-bold text-slate-500 leading-relaxed uppercase pr-4">You have reached {goalPercentage}% of your current event capacity limit.</p>
            </div>
          </div>
          
          <div className="pt-8 flex justify-between items-center border-t border-slate-50 mt-8">
             <span className="text-[10px] text-slate-400 font-bold uppercase">Last update: 2m ago</span>
            <motion.button 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleGoalDetails}
              className="bg-[#0a0c10] text-white px-8 py-3 rounded-full text-xs font-black shadow-xl transition-all"
            >
              Details →
            </motion.button>
          </div>
        </motion.div>

        {/* Agenda Section */}
        <motion.div variants={itemVariants} className="col-span-12 lg:col-span-3 puzzle-card-light">
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-slate-800 text-xl font-black tracking-tight uppercase">Event Agenda</h2>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setAgendaDate(d => { const n = new Date(d); n.setMonth(n.getMonth() - 1); setSelectedDate(1); return n; })}
                className="w-7 h-7 rounded-lg flex items-center justify-center bg-slate-50 hover:bg-slate-100 transition-colors text-slate-500"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" /></svg>
              </button>
              <span className="text-xs font-black text-slate-800 min-w-[80px] text-center">
                {agendaDate.toLocaleString('en-US', { month: 'short', year: 'numeric' })}
              </span>
              <button
                onClick={() => setAgendaDate(d => { const n = new Date(d); n.setMonth(n.getMonth() + 1); setSelectedDate(1); return n; })}
                className="w-7 h-7 rounded-lg flex items-center justify-center bg-slate-50 hover:bg-slate-100 transition-colors text-slate-500"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" /></svg>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-7 gap-1 mb-8">
            {['S','M','T','W','T','F','S'].map((d, i) => (
              <span key={`wh-${i}`} className="text-[9px] font-black text-slate-400 uppercase text-center py-1">{d}</span>
            ))}
            {/* Leading blank cells */}
            {Array.from({ length: new Date(agendaDate.getFullYear(), agendaDate.getMonth(), 1).getDay() }).map((_, i) => (
              <span key={`blank-${i}`} />
            ))}
            {/* Days of month */}
            {Array.from({ length: new Date(agendaDate.getFullYear(), agendaDate.getMonth() + 1, 0).getDate() }).map((_, i) => {
              const dateNum = i + 1;
              const isToday =
                new Date().getDate() === dateNum &&
                new Date().getMonth() === agendaDate.getMonth() &&
                new Date().getFullYear() === agendaDate.getFullYear();
              const hasEvent = events.some(e => {
                if (!e.date) return false;
                const ed = new Date(e.date);
                return ed.getDate() === dateNum && ed.getMonth() === agendaDate.getMonth() && ed.getFullYear() === agendaDate.getFullYear();
              });
              return (
                <motion.div
                  key={dateNum}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => setSelectedDate(dateNum)}
                  className={`h-8 flex flex-col items-center justify-center rounded-lg text-[10px] font-black transition-all cursor-pointer relative ${
                    selectedDate === dateNum
                      ? 'bg-[#0a0c10] text-white shadow-lg'
                      : isToday
                      ? 'bg-amber-50 text-amber-600 border border-amber-200'
                      : 'text-slate-500 hover:bg-slate-50'
                  }`}
                >
                  {dateNum}
                  {hasEvent && (
                    <div className={`absolute bottom-0.5 w-1 h-1 rounded-full ${selectedDate === dateNum ? 'bg-white' : 'bg-amber-400'}`} />
                  )}
                </motion.div>
              );
            })}
          </div>

          <div className="space-y-4 overflow-y-auto max-h-64">
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">
              {agendaDate.toLocaleString('en-US', { month: 'short' })} {selectedDate}, {agendaDate.getFullYear()}
            </p>
            {events.filter(e => {
              if (!e.date) return false;
              const ed = new Date(e.date);
              return (
                ed.getDate() === selectedDate &&
                ed.getMonth() === agendaDate.getMonth() &&
                ed.getFullYear() === agendaDate.getFullYear()
              );
            }).length > 0
              ? events.filter(e => {
                  if (!e.date) return false;
                  const ed = new Date(e.date);
                  return (
                    ed.getDate() === selectedDate &&
                    ed.getMonth() === agendaDate.getMonth() &&
                    ed.getFullYear() === agendaDate.getFullYear()
                  );
                }).map((e, i) => (
                <motion.div
                  key={e._id || i}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + i * 0.08 }}
                  onClick={() => handleAgendaClick({ title: e.name, time: new Date(e.date).toLocaleDateString(), registered: e.registeredCount, capacity: e.capacity, location: "Main Hall" })}
                  className="flex gap-3 p-3 rounded-2xl group hover:bg-slate-50 transition-all cursor-pointer border border-transparent hover:border-slate-100 hover:shadow-sm"
                >
                  <div className="w-10 h-10 rounded-full border-2 border-slate-100 flex items-center justify-center bg-white transition-all group-hover:scale-110 group-hover:border-slate-200 text-slate-400 text-sm flex-shrink-0">
                    ◻
                  </div>
                  <div className="flex-1 space-y-0.5 min-w-0">
                    <h4 className="font-bold text-slate-800 text-xs leading-tight truncate">{e.name}</h4>
                    <p className="text-[9px] text-slate-400 font-bold uppercase tracking-widest">{new Date(e.date).toLocaleDateString()}</p>
                  </div>
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-slate-300 group-hover:text-[#0a0c10] transition-colors flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                </motion.div>
              ))
              : <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-8 text-slate-400 font-bold text-xs italic uppercase">No events on this day</motion.p>
            }
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}